// =====================================================================
// arrival-context.ts (WP1 Task 12, binding amendment A5) — reads one-shot
// "how did the user arrive here" signals off the URL:
//   - `?claimed=1`  — a demo claim just succeeded (claim-finish.ts).
//     ClaimedBanner (components/claimed-banner.tsx) is the actual consumer
//     for THIS param today — it owns its own read/strip via
//     react-router's useSearchParams so it works standalone. readArrival's
//     `claimed` field exists for callers that need to know without
//     mounting that component (e.g. deciding whether to also show the
//     `?draft` stub below on the same load).
//   - `?draft=<id>` — WP2's draft-preview handoff. Task 12 only stashes the
//     id and shows a placeholder notice ("we're getting your preview
//     ready") on overview.tsx; WP2 owns the real preview flow and will
//     extend this file rather than replace it — keep it small.
//
// Deliberately plain window.location/sessionStorage, not react-router
// hooks — this needs to be callable from a lazy useState initializer (a
// pure, synchronous read) without depending on being inside a Router.
//
// Internal sessionStorage key: 'pb.draft'.
// =====================================================================

const DRAFT_STASH_KEY = 'pb.draft';

export interface Arrival {
  draft?: string;
  claimed?: boolean;
}

/**
 * Reads `?draft=` and `?claimed=1` off the CURRENT URL. Has one side
 * effect: when a draft id is present, it's stashed to sessionStorage under
 * 'pb.draft' immediately, so it survives a later redirect/reload that drops
 * the query string before WP2's consumer gets to it. Safe to call multiple
 * times (idempotent re-stash of the same value); does not touch the URL.
 */
export function readArrival(): Arrival {
  const params = new URLSearchParams(window.location.search);
  const draft = params.get('draft');
  const claimed = params.get('claimed') === '1';

  if (draft) {
    try {
      sessionStorage.setItem(DRAFT_STASH_KEY, draft);
    } catch {
      // sessionStorage unavailable (private mode / disabled) — non-fatal,
      // the id is still readable off the URL for the rest of this load.
    }
  }

  const arrival: Arrival = {};
  if (draft) arrival.draft = draft;
  if (claimed) arrival.claimed = true;
  return arrival;
}

/**
 * Strips `?draft` and `?claimed` from the current URL (via
 * history.replaceState — no navigation) and clears the sessionStorage
 * stash. Call once, when a caller is done consuming the arrival signal.
 * Task 12 does not call this for `?draft` (that stub only stashes + shows a
 * notice — full consumption, including this cleanup, is WP2's job); it
 * exists now so WP2 has it ready.
 */
export function consumeArrival(): void {
  try {
    sessionStorage.removeItem(DRAFT_STASH_KEY);
  } catch {
    // non-fatal
  }

  const params = new URLSearchParams(window.location.search);
  const hadEither = params.has('draft') || params.has('claimed');
  if (!hadEither) return;

  params.delete('draft');
  params.delete('claimed');
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}
